import { IGetAllProductsResult } from '../actions/create-order/get-all-products.action';
import { IPostCartItems } from '../actions/create-order/post-cart.action';
import {
    IPostCreateOrderData,
    IPostCreateOrderResponseData,
    IPostCreateOrderResponseOrderData
} from '../actions/create-order/post-create-order.action';
import { ILxsOrderData } from '../modules/lxs-create-order/lxs-create-order.data';
import { mapBusinessUnitToText } from './map-business-unit-to-text';

type EtrackerProductInput = IGetAllProductsResult;

declare global {
    interface Window {
        _etrackerOnReady?: any;
        etCommerce: any;
        et_seg1?: string;
        et_seg2?: string;
        _etracker?: { sendEvent: (event: unknown) => void };
        et_UserDefinedEvent: new (objectName: string, category: string, action: string, type: string) => unknown;
    }
}
interface IEtrackerProduct {
    id: string;
    name: string;
    price: string;
    currency: string;
    category?: string[] | string;
}
interface IEtrackerOrder {
    orderNumber: string;
    status: string;
    orderPrice: string;
    currency: string;
    basket: IEtrackerBasket;
    customerGroup: string;
    deliveryConditions: string;
    paymentConditions: string;
}
interface IEtrackerBasket {
    id: string;
    products: IEtrackerOrderProduct[];
}
interface IEtrackerOrderProduct {
    product: IEtrackerProduct;
    quantity: number;
}

export interface IEtrackerGlobalSegments {
    seg1?: string; //Division
    seg2?: string; //SoldToName
}

const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_SEGMENT_VALUE = 'NA';

const CONVERSATION_START_EVENT_OBJECT = 'Service Agent Conversation';
const CONVERSATION_START_EVENT_CATEGORY = 'Service Agent';
const CONVERSATION_START_EVENT_TYPE = '';
const CONVERSATION_START_ACTION = 'Conversation Start';

enum eCommerceAction {
    Insert = 'insertToBasket',
    Remove = 'removeFromBasket',
    Order = 'order'
}

const trackOnReady = (callback: () => void): void => {
    window._etrackerOnReady = typeof window._etrackerOnReady === 'undefined' ? [] : window._etrackerOnReady;

    window._etrackerOnReady.push(callback);
};

const resolveSubmittedProduct = (
    responseObject: IPostCreateOrderResponseOrderData['OrderLines'][number],
    orderData: IPostCreateOrderData,
    productsData?: IGetAllProductsResult[]
): IGetAllProductsResult | undefined => {
    return productsData?.find(product => {
        if (product.ProductNumber !== responseObject.ProductNumber) {
            return false;
        }

        return product.Incoterm1 === orderData.Order.Incoterm1;
    });
};

const convertOrderLineToEtrackerProduct = (
    orderData: IPostCreateOrderData,
    responseData: IPostCreateOrderResponseOrderData,
    productsData?: IGetAllProductsResult[]
): IEtrackerProduct[] => {
    return responseData.OrderLines.map(responseObject => {
        const submittedProduct = resolveSubmittedProduct(responseObject, orderData, productsData);
        const productHierarchy: string[] = [
            submittedProduct?.Hierarchy1Name,
            submittedProduct?.Hierarchy2Name,
            submittedProduct?.Hierarchy3Name
        ].filter((value): value is string => Boolean(value));

        return {
            id: responseObject.ProductNumber,
            name: responseObject.ProductName,
            price: '1',
            currency: DEFAULT_CURRENCY,
            category: productHierarchy
        };
    });
};

const convertProductToEtrackerProduct = (product: EtrackerProductInput): IEtrackerProduct => {
    const productHierarchy: string[] = [
        product?.Hierarchy1Name,
        product?.Hierarchy2Name,
        product?.Hierarchy3Name
    ].filter((value): value is string => Boolean(value));

    return {
        id: product.ProductNumber,
        name: product.ProductName,
        price: '1',
        currency: DEFAULT_CURRENCY,
        category: productHierarchy
    };
};

const matchesTrackedProduct = (candidate: IGetAllProductsResult, item: IPostCartItems, orderData: ILxsOrderData): boolean => {
    if (candidate.ProductNumber !== item.ProductNumber) {
        return false;
    }

    if (candidate.Incoterm1 !== orderData.orderHeader?.Incoterm1) {
        return false;
    }

    return true;
};

export const resolveTrackedProduct = (
    item: IPostCartItems,
    productsData: IGetAllProductsResult[] | undefined,
    orderData: ILxsOrderData
): IGetAllProductsResult | undefined => {
    return productsData?.find(candidate => matchesTrackedProduct(candidate, item, orderData));
};

export const resolveTrackedProducts = (
    items: IPostCartItems[],
    productsData: IGetAllProductsResult[] | undefined,
    orderData: ILxsOrderData
): IGetAllProductsResult[] => {
    return items.reduce<IGetAllProductsResult[]>((resolvedItems, item) => {
        const resolvedItem = resolveTrackedProduct(item, productsData, orderData);

        if (resolvedItem) {
            resolvedItems.push(resolvedItem);
        }

        return resolvedItems;
    }, []);
};

const convertOrderToEtrackerOrder = (
    orderData: IPostCreateOrderData,
    responseData: IPostCreateOrderResponseData,
    productsData?: IGetAllProductsResult[]
): IEtrackerOrder => {
    const etrackerProducts: IEtrackerProduct[] = convertOrderLineToEtrackerProduct(orderData, responseData.Order, productsData);

    const etrackerOrderProducts: IEtrackerOrderProduct[] = etrackerProducts.map(product => {
        return {
            product: product,
            quantity: 1
        } as IEtrackerOrderProduct;
    });

    return {
        orderNumber: responseData.Order.OrderNumber,
        status: 'sale',
        orderPrice: '1',
        currency: DEFAULT_CURRENCY,
        basket: {
            id: responseData.Order.OrderNumber,
            products: etrackerOrderProducts
        },
        customerGroup: mapBusinessUnitToText(orderData.Order.Division),
        deliveryConditions: '',
        paymentConditions: ''
    };
};

const isEtrackerReady = (): boolean => typeof window.etCommerce?.sendEvent === 'function' && !Array.isArray(window.etCommerce);

const normalizeSegmentValue = (value?: string): string => {
    const normalized = value?.trim();
    return normalized ? normalized : DEFAULT_SEGMENT_VALUE;
};

export const setGlobalDimensionsFromMap = (segments: IEtrackerGlobalSegments) => {
    setGlobalDimensions([normalizeSegmentValue(segments.seg1), normalizeSegmentValue(segments.seg2)]);
};

const _sendProductIfReady = (
    action: eCommerceAction,
    payload: IEtrackerProduct,
    quantity?: number,
    et_seg1?: string,
    et_seg2?: string
): void => {
    if (!isEtrackerReady()) {
        return;
    }
    const normalizedSegments: IEtrackerGlobalSegments = {
        seg1: normalizeSegmentValue(et_seg1),
        seg2: normalizeSegmentValue(et_seg2)
    };

    if (typeof quantity === 'number') {
        setGlobalDimensionsFromMap(normalizedSegments);
        window.etCommerce.sendEvent(action, payload, quantity, normalizedSegments.seg1, normalizedSegments.seg2);
    }
};

const _sendOrderIfReady = (action: eCommerceAction, payload: IEtrackerOrder, et_seg1?: string, et_seg2?: string): void => {
    if (!isEtrackerReady()) {
        return;
    }

    const normalizedSegments: IEtrackerGlobalSegments = {
        seg1: normalizeSegmentValue(et_seg1),
        seg2: normalizeSegmentValue(et_seg2)
    };

    setGlobalDimensionsFromMap(normalizedSegments);
    window.etCommerce.sendEvent(action, payload, normalizedSegments.seg1, normalizedSegments.seg2);
};

const _sendConversationStartIfReady = (et_seg1: string, et_seg2: string): void => {
    const normalizedSegments: IEtrackerGlobalSegments = {
        seg1: normalizeSegmentValue(et_seg1),
        seg2: normalizeSegmentValue(et_seg2)
    };
    setGlobalDimensionsFromMap(normalizedSegments);
    if (typeof window.et_UserDefinedEvent !== 'function' || typeof window._etracker?.sendEvent !== 'function') {
        return;
    }
    window._etracker.sendEvent(
        new window.et_UserDefinedEvent(
            CONVERSATION_START_EVENT_OBJECT,
            CONVERSATION_START_EVENT_CATEGORY,
            CONVERSATION_START_ACTION,
            CONVERSATION_START_EVENT_TYPE
        )
    );
};

export const trackInsertToBasket = (product: IGetAllProductsResult, orderData: ILxsOrderData) => {
    const convertedProduct = convertProductToEtrackerProduct(product);
    const et_seg1 = mapBusinessUnitToText(product.Division) || DEFAULT_SEGMENT_VALUE;
    const et_seg2 = orderData.soldTo.Name1 || DEFAULT_SEGMENT_VALUE;
    _sendProductIfReady(eCommerceAction.Insert, convertedProduct, 1, et_seg1, et_seg2);
};

export const trackRemoveSingleItemFromBasket = (product: IGetAllProductsResult, orderData?: ILxsOrderData) => {
    const convertedProduct = convertProductToEtrackerProduct(product);
    let et_seg1: string | undefined = DEFAULT_SEGMENT_VALUE;
    let et_seg2: string = DEFAULT_SEGMENT_VALUE;
    if (orderData?.orderHeader?.Division) {
        et_seg1 = mapBusinessUnitToText(orderData.orderHeader.Division);
    }
    et_seg2 = orderData?.soldTo.Name1 || DEFAULT_SEGMENT_VALUE;
    _sendProductIfReady(eCommerceAction.Remove, convertedProduct, 1, et_seg1, et_seg2);
};

export const trackRemoveItemsFromBasket = (items: IGetAllProductsResult[], orderData: ILxsOrderData) => {
    const { orderHeader, soldTo } = orderData;
    items.forEach(item => {
        const convertedProduct = convertProductToEtrackerProduct(item);
        let et_seg1: string | undefined = DEFAULT_SEGMENT_VALUE;
        let et_seg2: string | undefined = DEFAULT_SEGMENT_VALUE;
        if (orderHeader?.Division) {
            et_seg1 = mapBusinessUnitToText(orderHeader.Division);
        }
        et_seg2 = soldTo.Name1 || DEFAULT_SEGMENT_VALUE;
        _sendProductIfReady(eCommerceAction.Remove, convertedProduct, 1, et_seg1, et_seg2);
    });
};

/**
 *
 * @param dimensionValues [0] = et_seg1 (Division/BusinessUnit), [1] = et_seg2 (SoldTo-Name) etc
 */
export const setGlobalDimensions = (dimensionValues: string[]) => {
    dimensionValues.map((value: string, index: number) => {
        const dimensionIndex = index + 1;
        (window as any)[`et_seg${dimensionIndex}`] = normalizeSegmentValue(value);
    });
};

export const trackSubmittedOrder = (
    orderData: IPostCreateOrderData,
    responseData: IPostCreateOrderResponseData,
    productsData?: IGetAllProductsResult[]
) => {
    const convertedOrder = convertOrderToEtrackerOrder(orderData, responseData, productsData);
    const et_seg1 = convertedOrder.customerGroup ?? DEFAULT_SEGMENT_VALUE;
    const et_seg2 = responseData.Order.SoldTo.Name1 ?? DEFAULT_SEGMENT_VALUE;
    trackOnReady(() => {
        _sendOrderIfReady(eCommerceAction.Order, convertedOrder, et_seg1, et_seg2);
    });
};

export const trackConversationStart = (): void => {
    const et_seg1 = DEFAULT_SEGMENT_VALUE;
    const et_seg2 = DEFAULT_SEGMENT_VALUE;
    _sendConversationStartIfReady(et_seg1, et_seg2);
};
